// PII redaction helpers used by PostHog ingest paths (event properties +
// captured exception payloads + Inngest middleware). CLAUDE.md: PostHog must
// never receive raw family data — extend the regex set / SENSITIVE_FIELD_NAMES
// here when adding a new sensitive field, and both `lib/posthog/scrub.ts`
// (event properties) and `lib/posthog/server.ts` (exception messages + stacks)
// stay in sync.

const PHONE_REGEX = /\+\d{10,15}/g
// CLAUDE.md lists emails as private family data. Catches RFC-loose addresses
// embedded in error messages ("User foo@bar.com not found"), URLs, and JSON
// values. Two-char TLD floor avoids false-matching version strings (1.2.3).
const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g
const JWT_REGEX = /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g
// LINE userIds are opaque IDs starting with U followed by 32 hex chars.
const LINE_USER_ID_REGEX = /\bU[a-f0-9]{32}\b/g
// MOD-103: redact storyteller invitation tokens. The token is a 43-char
// base64url string that appears in three known places:
//   1. URL path right after /storyteller/claim/ (claim route)
//   2. `t=` query param on /storyteller/claim/expired
//   3. `token=` form field POSTed to /api/v1/storyteller-invitations/reissue
// All three regexes anchor on a known prefix so they don't false-match
// unrelated 43-char identifiers (JWT segments, slugs, request ids).
const STORYTELLER_CLAIM_TOKEN_REGEX = /\/storyteller\/claim\/[A-Za-z0-9_-]{43}/g
const STORYTELLER_CLAIM_T_PARAM_REGEX = /([?&]t=)[A-Za-z0-9_-]{43}/g
// `token` field in form-urlencoded body, JSON serialization, or quoted
// JSON string. Catches `token=<tok>`, `"token":"<tok>"`, `token: "<tok>"`.
const STORYTELLER_TOKEN_FIELD_REGEX =
  /(["']?token["']?\s*[:=]\s*["']?)[A-Za-z0-9_-]{43}/g
// BAO-129: a Stripe Checkout session id in a URL query. PostHog captures
// `$current_url` with the query on every history change, and both
// /gift/thank-you and /onboarding/welcome carry `?session_id=cs_...`, which
// the thank-you route turns into a read of a gift row. Redact the value,
// keep the parameter so the pageview is still recognizable.
const STRIPE_SESSION_PARAM_REGEX = /([?&]session_id=)cs_(?:test|live)_[A-Za-z0-9]+/g
const SENSITIVE_HEADERS = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-supabase-auth',
  'x-twilio-signature',
  'x-line-signature',
])
// Field names whose values should always be redacted, regardless of where they appear.
const SENSITIVE_FIELD_NAMES = new Set([
  'line_user_id',
  'lineuserid',
  'line_media_id',
  'linemediaid',
  'replytoken',
  'reply_token',
  'webhookeventid',
  'webhook_event_id',
  'signed_storyteller_id',
  'id_token',
  'liff_id_token',
  'lineoauthurl',
  'line_oauth_url',
  'lineoabasicid',
  'line_oa_basic_id',
])

export function redactString(s: string): string {
  return s
    .replace(PHONE_REGEX, '[phone]')
    .replace(EMAIL_REGEX, '[email]')
    .replace(JWT_REGEX, '[jwt]')
    .replace(LINE_USER_ID_REGEX, '[line_user]')
    .replace(STORYTELLER_CLAIM_TOKEN_REGEX, '/storyteller/claim/[redacted]')
    .replace(STORYTELLER_CLAIM_T_PARAM_REGEX, '$1[redacted]')
    .replace(STORYTELLER_TOKEN_FIELD_REGEX, '$1[redacted]')
    .replace(STRIPE_SESSION_PARAM_REGEX, '$1[redacted]')
}

export function redactSensitive(value: unknown): unknown {
  if (typeof value === 'string') return redactString(value)
  if (Array.isArray(value)) return value.map(redactSensitive)
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (SENSITIVE_FIELD_NAMES.has(k.toLowerCase())) {
        out[k] = '[redacted]'
      } else {
        out[k] = redactSensitive(v)
      }
    }
    return out
  }
  return value
}

type HeaderValue = string | string[] | undefined
type Headers = Record<string, HeaderValue>

export function scrubHeaders(headers: Headers): Headers {
  const out: Headers = {}
  for (const [k, v] of Object.entries(headers)) {
    out[k] = SENSITIVE_HEADERS.has(k.toLowerCase()) ? '[redacted]' : v
  }
  return out
}

export const __testing = { redactString, redactSensitive, scrubHeaders }
